import { Fragment, type ReactNode } from 'react';

const URL_PATTERN = '(?:https?:\\/\\/[^\\s]+|www\\.[^\\s]+)';
const EMAIL_PATTERN = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}';
const ADDRESS_SUFFIXES = 'Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Way|Place|Pl|Square|Sq|Terrace|Ter|Parkway|Pkwy|Circle|Cir|Highway|Hwy';
const ADDRESS_TAIL_SEGMENT = "(?:,\\s*[A-Za-z0-9#.'\\s-]{1,40})";
const ADDRESS_PATTERN = `\\d{1,6}\\s+[A-Za-z0-9.'\\s]{2,40}?\\b(?:${ADDRESS_SUFFIXES})\\b\\.?(?:${ADDRESS_TAIL_SEGMENT}){0,6}`;
const PHONE_PATTERN = '(?:\\+?1[-.\\s]?)?\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}';

const MASTER_REGEX = new RegExp(`(${URL_PATTERN}|${EMAIL_PATTERN}|${ADDRESS_PATTERN}|${PHONE_PATTERN})`, 'i');

const ADDRESS_SUFFIX_REGEX = new RegExp(`\\b(?:${ADDRESS_SUFFIXES})\\b\\.?`, 'i');
const TRAILING_PUNCT_REGEX = /[.,!?;:)\]}]+$/;

function splitTrailingPunctuation(text: string): [string, string] {
    const match = text.match(TRAILING_PUNCT_REGEX);
    if (!match) return [text, ''];
    return [text.slice(0, -match[0].length), match[0]];
}

type MatchKind = 'url' | 'email' | 'address' | 'phone';

function classify(match: string): MatchKind {
    if (/^https?:\/\//i.test(match) || /^www\./i.test(match)) return 'url';
    if (match.includes('@')) return 'email';
    if (ADDRESS_SUFFIX_REGEX.test(match)) return 'address';
    return 'phone';
}

const linkClassName = 'text-primary40 underline break-all';

function renderMatch(rawMatch: string, key: number): ReactNode {
    const [match, trailing] = splitTrailingPunctuation(rawMatch);
    const kind = classify(match);

    let link: ReactNode;
    switch (kind) {
        case 'url': {
            const href = match.toLowerCase().startsWith('www.') ? `https://${match}` : match;
            link = (
                <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
                    {match}
                </a>
            );
            break;
        }
        case 'email':
            link = (
                <a href={`mailto:${match}`} className={linkClassName}>
                    {match}
                </a>
            );
            break;
        case 'phone':
            link = (
                <a href={`tel:${match.replace(/[^\d+]/g, '')}`} className={linkClassName}>
                    {match}
                </a>
            );
            break;
        case 'address':
            link = (
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(match)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                >
                    {match}
                </a>
            );
            break;
    }

    return (
        <Fragment key={key}>
            {link}
            {trailing}
        </Fragment>
    );
}

export function renderTextWithLinks(text?: string | null): ReactNode {
    if (!text) return null;

    return text.split('\n').map((line, lineIndex) => (
        <Fragment key={lineIndex}>
            {lineIndex > 0 && <br />}
            {line.split(MASTER_REGEX).map((part, partIndex) => {
                if (partIndex % 2 === 0 || !part) return part;
                return renderMatch(part, partIndex);
            })}
        </Fragment>
    ));
}
